import React from 'react';
import './CommonModal.scss';

interface CommonModalProps {
  isOpen: boolean;                 // Whether the modal is open or closed
  onClose: () => void;             // Function to close the modal
  title?: string;                  // Modal title (optional)
  bodyContent?: React.ReactNode;   // Modal body content
  footerContent?: React.ReactNode; // Optional footer content (e.g., action buttons)
  size?: 'small' | 'medium' | 'large';  // Size of the modal (default is 'medium')
  customClass?: string;            // Custom class for styling
  overlayBackground?: string;     // Background color of the overlay (default is 'rgba(0, 0, 0, 0.5)')
  closeOnOverlayClick?: boolean;  // Whether the modal should close if you click on the overlay
  loading?: boolean;              // Optional loading state for the modal
  loadingText?: string;           // Text to show when loading is true
}

const CommonModal: React.FC<CommonModalProps> = ({
  isOpen,
  onClose,
  title,
  bodyContent,
  footerContent,
  size = 'medium',
  customClass = '',
  overlayBackground = 'rgba(0, 0, 0, 0.5)',
  closeOnOverlayClick = true,
  loading = false,
  loadingText = 'Loading...',
}: CommonModalProps) => {
  if (!isOpen) return null;

  // Handle click on overlay to close modal if closeOnOverlayClick is true
  const handleOverlayClick = (e: React.MouseEvent) => {
    if (closeOnOverlayClick) {
      onClose();
    }
  };

  return (
    <div
      className="modal-overlay"
      onClick={handleOverlayClick}
      style={{ backgroundColor: overlayBackground }}
    >
      <div
        className={`modal-container ${size} ${customClass}`}
        onClick={(e) => e.stopPropagation()} // Prevent clicking inside the modal from closing it
      >
        {/* Modal Header */}
        <div className="modal-header">
          {title && <h2>{title}</h2>}
          <button className="modal-close" onClick={onClose}>
            ×
          </button>
        </div>

        {/* Modal Body (Content) */}
        <div className="modal-body">
          {loading ? (
            <div className="modal-loading">
              <div className="loading-spinner"></div>
              <span>{loadingText}</span>
            </div>
          ) : (
            bodyContent
          )}
        </div>

        {/* Modal Footer (if provided) */}
        {footerContent && (
          <div className="modal-footer">
            {footerContent}
          </div>
        )}
      </div>
    </div>
  );
};

export default CommonModal;
